/**
 * FanModalClassic — Monolithic ceiling fan device modal template.
 *
 * Complete fan control panel:
 * - Power toggle (On/Off)
 * - Speed selector (Low / Med / High / Auto)
 * - Rotation toggle (oscillation on/off)
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './FanModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const FAN_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Power Section ──
  { id: 'power', label: 'Power', icon: 'power_settings_new', slots: [
    { id: 'power-label', label: 'Title "POWER STATE"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'power-value', label: 'Status Text "ON/OFF"', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-btn', label: 'Power Button Circle', editableProps: ['background', 'color', 'borderRadius', 'width', 'height', 'boxShadow', 'glow'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-btn-icon', label: 'Power Button Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
  ]},
  // ── Speed Section ──
  { id: 'speed', label: 'Fan Speed', icon: 'mode_fan', slots: [
    { id: 'speed-label', label: 'Title "SPEED"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'speed-label-icon', label: 'Speed Title Icon', editableProps: ['fontSize', 'color'] },
    { id: 'speed-btn', label: 'Speed Button', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize', 'border', 'glow'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
    { id: 'speed-btn-text', label: 'Speed Button Text', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  // ── Rotation Section ──
  { id: 'rotation', label: 'Oscillation', icon: 'rotate_right', slots: [
    { id: 'rotation-label', label: 'Title "OSCILLATION"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'rotation-value', label: 'Status "ON/OFF"', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'rotation-btn', label: 'Oscillation Toggle Button', editableProps: ['background', 'color', 'borderRadius', 'padding', 'border', 'boxShadow'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'rotation-btn-icon', label: 'Oscillation Button Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
  ]},
]

const DEFAULT_SECTION_ORDER = FAN_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FAN_SPEEDS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Med' },
  { id: 'high', label: 'High' },
  { id: 'auto', label: 'Auto' },
] as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FanModalClassic({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const isOn = getAttr(deviceState, 'on', 'isOn', 'powerState') === true
    || strAttr(deviceState, 'on', 'isOn', 'powerState') === 'true'
  const currentSpeed = strAttr(deviceState, 'currentFanSpeedSetting', 'fanSpeed', 'speed') || 'auto'
  const isOscillating = getAttr(deviceState, 'fanOscillationMode', 'oscillation', 'rotation') === true
    || strAttr(deviceState, 'fanOscillationMode', 'oscillation', 'rotation') === 'true'
    || strAttr(deviceState, 'fanOscillationMode', 'oscillation', 'rotation') === 'oscillate'

  // ---- Commands ----
  const togglePower = useCallback(() => {
    if (isEditorMode) return
    onCommand(isOn ? 'off' : 'on')
  }, [isEditorMode, isOn, onCommand])

  const setSpeed = useCallback((speed: string) => {
    if (isEditorMode) return
    onCommand('setFanSpeed', { fanSpeed: speed })
  }, [isEditorMode, onCommand])

  const toggleRotation = useCallback(() => {
    if (isEditorMode) return
    onCommand('setRotation', { oscillation: !isOscillating })
  }, [isEditorMode, isOscillating, onCommand])

  return (
    <div
      className={`fan-modal-classic${isOn ? '' : ' is-off'}${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="fan-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="fan-modal-classic__header">
        <button className="fan-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="fan-modal-classic__title" data-modal-slot="header-title">{device.name || 'Ceiling Fan'}</h2>
        <button
          className="fan-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'power':
            return (
              <div key="power" className={`fan-modal-classic__power-section${hl}`} data-modal-section="power">
                <div className="fan-modal-classic__power-label" data-modal-slot="power-label">POWER STATE</div>
                <div className="fan-modal-classic__power-row">
                  <span
                    className={`fan-modal-classic__power-status${isOn ? ' on' : ' off'}`}
                    data-modal-slot="power-value"
                    data-modal-slot-state={isOn ? 'on' : 'off'}
                  >
                    {isOn ? 'ON' : 'OFF'}
                  </span>
                  <button
                    className={`fan-modal-classic__power-btn${isOn ? ' on' : ''}`}
                    data-modal-slot="power-btn"
                    data-modal-slot-state={isOn ? 'on' : 'off'}
                    onClick={togglePower}
                    aria-label={isOn ? 'Turn off' : 'Turn on'}
                  >
                    <span
                      className="material-symbols-rounded"
                      data-modal-slot="power-btn-icon"
                      data-modal-slot-state={isOn ? 'on' : 'off'}
                    >
                      power_settings_new
                    </span>
                  </button>
                </div>
              </div>
            )
          case 'speed':
            return (
              <div key="speed" className={`fan-modal-classic__speed-section${hl}`} data-modal-section="speed">
                <div className="fan-modal-classic__section-label">
                  <span data-modal-slot="speed-label">SPEED</span>
                  <span className="material-symbols-rounded fan-modal-classic__speed-icon" data-modal-slot="speed-label-icon">mode_fan</span>
                </div>
                <div className="fan-modal-classic__speed-grid">
                  {FAN_SPEEDS.map((speed) => (
                    <button
                      key={speed.id}
                      className={`fan-modal-classic__speed-btn${currentSpeed === speed.id ? ' active' : ''}`}
                      data-modal-slot="speed-btn"
                      data-modal-slot-state={currentSpeed === speed.id ? 'active' : 'default'}
                      onClick={() => setSpeed(speed.id)}
                    >
                      <span
                        data-modal-slot="speed-btn-text"
                        data-modal-slot-state={currentSpeed === speed.id ? 'active' : 'default'}
                      >
                        {speed.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )
          case 'rotation':
            return (
              <div key="rotation" className={`fan-modal-classic__rotation-section${hl}`} data-modal-section="rotation">
                <div className="fan-modal-classic__rotation-label" data-modal-slot="rotation-label">OSCILLATION</div>
                <div className="fan-modal-classic__rotation-row">
                  <span
                    className={`fan-modal-classic__rotation-value${isOscillating ? ' on' : ' off'}`}
                    data-modal-slot="rotation-value"
                    data-modal-slot-state={isOscillating ? 'on' : 'off'}
                  >
                    {isOscillating ? 'ON' : 'OFF'}
                  </span>
                  <button
                    className={`fan-modal-classic__rotation-btn${isOscillating ? ' on' : ''}`}
                    data-modal-slot="rotation-btn"
                    data-modal-slot-state={isOscillating ? 'on' : 'off'}
                    onClick={toggleRotation}
                    aria-label={isOscillating ? 'Disable oscillation' : 'Enable oscillation'}
                  >
                    <span
                      className="material-symbols-rounded"
                      data-modal-slot="rotation-btn-icon"
                      data-modal-slot-state={isOscillating ? 'on' : 'off'}
                    >
                      rotate_right
                    </span>
                  </button>
                </div>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
